import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { SourceFieldMapping, DataElementView } from "@/entities/all";
import ElementCombobox from './ElementCombobox';

export default forwardRef(function EditMappingModal({ isOpen, onClose, onMappingUpdated, mapping, onRequestNewElement }, ref) {
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const pendingElementIdRef = useRef(null);

  const [elements, setElements] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load data on mount (component is only rendered when modal is open)
  const dataLoadedRef = useRef(false);
  useEffect(() => {
    if (!dataLoadedRef.current) {
      dataLoadedRef.current = true;
      loadData();
      if (mapping) {
        setFormData({
          source_id: mapping.source_id || '',
          canonical_element_id: mapping.canonical_element_id || '',
          source_field_name: mapping.source_field_name || '',
          transformation_notes: mapping.transformation_notes || '',
          mapping_confidence: mapping.mapping_confidence || 0.8,
          last_validated: mapping.last_validated ? mapping.last_validated.split('T')[0] : new Date().toISOString().split('T')[0]
        });
      }
    }
  }, []);

  // Apply pending element selection after elements list updates
  useEffect(() => {
    if (pendingElementIdRef.current && elements.length > 0) {
      const id = pendingElementIdRef.current;
      pendingElementIdRef.current = null;
      setFormData(prev => ({ ...prev, canonical_element_id: id }));
    }
  }, [elements]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const elementsData = await DataElementView.list('canonical_name');
      setElements(elementsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Called from parent after new element is created
  const refreshAndSelectElement = useCallback(async (newElement) => {
    const elementsData = await DataElementView.list('canonical_name');
    const createdInView = elementsData.find(e => e.canonical_name === newElement?.canonical_name) ||
                          elementsData.find(e => e.id === newElement?.id);
    if (createdInView) {
      pendingElementIdRef.current = createdInView.id;
    }
    setElements(elementsData);
  }, []);

  useImperativeHandle(ref, () => ({ refreshAndSelectElement }), [refreshAndSelectElement]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await SourceFieldMapping.update(mapping.id, formData);
      const selectedElement = elements.find(e => e.id === formData.canonical_element_id);
      onMappingUpdated({
        ...formData,
        element_canonical_name: selectedElement?.canonical_name || '',
        element_data_type: selectedElement?.data_type || '',
      });
    } catch (error) {
      console.error('Error updating mapping:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleElementChange = useCallback((id) => {
    setFormData(prev => ({ ...prev, canonical_element_id: id }));
  }, []);

  if (isLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Field Mapping: {mapping?.source_field_name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          <div>
            <Label htmlFor="edit_canonical_element_id">Canonical Data Element</Label>
            <ElementCombobox
              elements={elements}
              value={formData.canonical_element_id}
              onValueChange={handleElementChange}
              onRequestNew={onRequestNewElement}
              placeholder="Select canonical element"
            />
          </div>

          <div>
            <Label htmlFor="edit_transformation_notes">Transformation Notes</Label>
            <Textarea
              id="edit_transformation_notes"
              value={formData.transformation_notes}
              onChange={(e) => handleChange('transformation_notes', e.target.value)}
              rows={3}
            />
          </div>

          <div>
            <Label>Mapping Confidence: {((formData.mapping_confidence || 0) * 100).toFixed(0)}%</Label>
            <Slider
              value={[formData.mapping_confidence || 0]}
              onValueChange={(value) => handleChange('mapping_confidence', value[0])}
              max={1} min={0} step={0.05}
              className="mt-2"
            />
          </div>

          <div>
            <Label htmlFor="edit_last_validated">Last Validated</Label>
            <Input
              id="edit_last_validated"
              type="date"
              value={formData.last_validated}
              onChange={(e) => handleChange('last_validated', e.target.value)}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Updating...' : 'Update Mapping'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
});
